import { apiCall } from "../api";
import { showToast } from "../utils";

function getEl<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}
function getSelect(id: string): HTMLSelectElement | null {
  return getEl<HTMLSelectElement>(id);
}
function getInput(id: string): HTMLInputElement | null {
  return getEl<HTMLInputElement>(id);
}

const NEW_OPTION = "__new__";
const EMPTY_OPTION = "";

export interface ClassificationPickerValue {
  group_id: number;
  group_name: string;
  class_id?: number;
  class_name?: string;
  subclass_id?: number;
  subclass_name?: string;
}

interface GroupItem {
  group_id: number;
  group_key: string;
  group_name: string;
}

export class ClassificationPicker {
  modal: HTMLElement;
  groupSelect: HTMLSelectElement;
  classSelect: HTMLSelectElement;
  subclassSelect: HTMLSelectElement;
  newGroupWrap: HTMLElement;
  newClassWrap: HTMLElement;
  newSubclassWrap: HTMLElement;
  newGroupInput: HTMLInputElement;
  newClassInput: HTMLInputElement;
  newSubclassInput: HTMLInputElement;
  saveBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;

  groups: GroupItem[] = [];
  classes: { class_id: number; class_name: string }[] = [];
  subclasses: { subclass_id: number; subclass_name: string }[] = [];

  onSave: ((value: ClassificationPickerValue) => void) | null = null;

  constructor() {
    this.modal = getEl<HTMLElement>("classification-picker-modal")!;
    this.groupSelect = getSelect("class-picker-group")!;
    this.classSelect = getSelect("class-picker-class")!;
    this.subclassSelect = getSelect("class-picker-subclass")!;
    this.newGroupWrap = getEl<HTMLElement>("class-picker-new-group-wrap")!;
    this.newClassWrap = getEl<HTMLElement>("class-picker-new-class-wrap")!;
    this.newSubclassWrap = getEl<HTMLElement>("class-picker-new-subclass-wrap")!;
    this.newGroupInput = getInput("class-picker-new-group")!;
    this.newClassInput = getInput("class-picker-new-class")!;
    this.newSubclassInput = getInput("class-picker-new-subclass")!;
    this.saveBtn = getEl<HTMLButtonElement>("class-picker-save")!;
    this.cancelBtn = getEl<HTMLButtonElement>("class-picker-cancel")!;

    this.bindEvents();
  }

  private bindEvents(): void {
    this.cancelBtn.onclick = () => this.close();
    this.saveBtn.onclick = () => this.handleSave();

    this.groupSelect.onchange = () => {
      this.toggleNewInput(this.groupSelect, this.newGroupWrap);
      void this.loadClasses();
    };
    this.classSelect.onchange = () => {
      this.toggleNewInput(this.classSelect, this.newClassWrap);
      void this.loadSubclasses();
    };
    this.subclassSelect.onchange = () => {
      this.toggleNewInput(this.subclassSelect, this.newSubclassWrap);
    };
  }

  private toggleNewInput(select: HTMLSelectElement, wrap: HTMLElement): void {
    wrap.style.display = select.value === NEW_OPTION ? "flex" : "none";
  }

  async open(options: {
    value?: Partial<ClassificationPickerValue>;
    onSave: (value: ClassificationPickerValue) => void;
  }): Promise<void> {
    this.onSave = options.onSave;
    this.newGroupInput.value = "";
    this.newClassInput.value = "";
    this.newSubclassInput.value = "";
    this.newGroupWrap.style.display = "none";
    this.newClassWrap.style.display = "none";
    this.newSubclassWrap.style.display = "none";

    await this.loadGroups();

    const value = options.value || {};
    if (value.group_id) {
      this.groupSelect.value = String(value.group_id);
      await this.loadClasses();
      if (value.class_id) {
        this.classSelect.value = String(value.class_id);
        await this.loadSubclasses();
        if (value.subclass_id) {
          this.subclassSelect.value = String(value.subclass_id);
        } else {
          this.subclassSelect.value = EMPTY_OPTION;
        }
      } else {
        this.classSelect.value = EMPTY_OPTION;
        this.subclassSelect.innerHTML = '<option value="">-- не выбрано --</option>';
      }
    } else {
      this.groupSelect.value = EMPTY_OPTION;
      this.classSelect.innerHTML = '<option value="">-- не выбрано --</option>';
      this.subclassSelect.innerHTML = '<option value="">-- не выбрано --</option>';
    }

    this.modal.style.display = "flex";
  }

  close(): void {
    this.modal.style.display = "none";
    this.onSave = null;
  }

  private async loadGroups(): Promise<void> {
    const res = await apiCall("/api/groups", "GET");
    this.groups = (res?.groups || []) as GroupItem[];
    let html = '<option value="">-- не выбрано --</option>';
    html += this.groups
      .map((g) => `<option value="${g.group_id}">${g.group_name}</option>`)
      .join("");
    html += `<option value="${NEW_OPTION}">+ Новая группа</option>`;
    this.groupSelect.innerHTML = html;
  }

  private async loadClasses(): Promise<void> {
    this.classSelect.innerHTML = '<option value="">-- не выбрано --</option>';
    this.subclassSelect.innerHTML = '<option value="">-- не выбрано --</option>';
    const groupId = this.groupSelect.value;
    if (!groupId || groupId === NEW_OPTION) return;

    const res = await apiCall(`/api/admin/taxonomy/classes?group_id=${groupId}`, "GET");
    this.classes = (res?.classes || []) as { class_id: number; class_name: string }[];
    let html = '<option value="">-- не выбрано --</option>';
    html += this.classes
      .map((c) => `<option value="${c.class_id}">${c.class_name}</option>`)
      .join("");
    html += `<option value="${NEW_OPTION}">+ Новый класс</option>`;
    this.classSelect.innerHTML = html;
  }

  private async loadSubclasses(): Promise<void> {
    this.subclassSelect.innerHTML = '<option value="">-- не выбрано --</option>';
    const classId = this.classSelect.value;
    if (!classId || classId === NEW_OPTION) return;

    const res = await apiCall(`/api/admin/taxonomy/subclasses?class_id=${classId}`, "GET");
    this.subclasses = (res?.subclasses || []) as { subclass_id: number; subclass_name: string }[];
    let html = '<option value="">-- не выбрано --</option>';
    html += this.subclasses
      .map((s) => `<option value="${s.subclass_id}">${s.subclass_name}</option>`)
      .join("");
    html += `<option value="${NEW_OPTION}">+ Новый подкласс</option>`;
    this.subclassSelect.innerHTML = html;
  }

  private async handleSave(): Promise<void> {
    let groupId = parseInt(this.groupSelect.value, 10);
    let groupName = "";

    if (this.groupSelect.value === NEW_OPTION) {
      groupName = this.newGroupInput.value.trim();
      if (!groupName) {
        showToast("Укажите название новой группы", "error");
        return;
      }
      const res = await apiCall("/api/admin/taxonomy/groups", "POST", { group_name: groupName });
      if (!res || !res.group_id) {
        showToast("Ошибка создания группы", "error");
        return;
      }
      groupId = res.group_id;
      groupName = res.group_name || groupName;
    } else {
      const group = this.groups.find((g) => g.group_id === groupId);
      if (!group) {
        showToast("Группа не выбрана", "error");
        return;
      }
      groupName = group.group_name;
    }

    let classId: number | undefined;
    let className: string | undefined;
    if (this.classSelect.value === NEW_OPTION) {
      className = this.newClassInput.value.trim();
      if (!className) {
        showToast("Укажите название нового класса", "error");
        return;
      }
      const res = await apiCall("/api/admin/taxonomy/classes", "POST", { group_id: groupId, class_name: className });
      if (!res || !res.class_id) {
        showToast("Ошибка создания класса", "error");
        return;
      }
      classId = res.class_id;
      className = res.class_name || className;
    } else if (this.classSelect.value) {
      classId = parseInt(this.classSelect.value, 10);
      const cls = this.classes.find((c) => c.class_id === classId);
      className = cls?.class_name;
    }

    let subclassId: number | undefined;
    let subclassName: string | undefined;
    if (this.subclassSelect.value === NEW_OPTION) {
      if (!classId) {
        showToast("Сначала выберите класс для нового подкласса", "error");
        return;
      }
      subclassName = this.newSubclassInput.value.trim();
      if (!subclassName) {
        showToast("Укажите название нового подкласса", "error");
        return;
      }
      const res = await apiCall("/api/admin/taxonomy/subclasses", "POST", { class_id: classId, subclass_name: subclassName });
      if (!res || !res.subclass_id) {
        showToast("Ошибка создания подкласса", "error");
        return;
      }
      subclassId = res.subclass_id;
      subclassName = res.subclass_name || subclassName;
    } else if (this.subclassSelect.value) {
      subclassId = parseInt(this.subclassSelect.value, 10);
      const sub = this.subclasses.find((s) => s.subclass_id === subclassId);
      subclassName = sub?.subclass_name;
    }

    const value: ClassificationPickerValue = {
      group_id: groupId,
      group_name: groupName,
      class_id: classId,
      class_name: className,
      subclass_id: subclassId,
      subclass_name: subclassName,
    };

    if (this.onSave) {
      this.onSave(value);
    }
    this.close();
  }
}

export const classificationPicker = new ClassificationPicker();
